
import { Component, Vue, Prop, InjectReactive } from "vue-property-decorator"
import { Getter } from "vuex-class"

import { CaseDetails, TemplateDetails, User } from "@/types"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import CaseDetailsHeader from "@/components/CaseDetails/CaseDetailsHeader.vue"
import CaseSideBar from "./CaseSideBar.vue"
import CaseFields from "./CaseFields.vue"
import CaseFiles from "@/components/CaseDetails/CaseFiles.vue"
import KeyEntries from "@/components/CaseDetails/KeyEntries.vue"
import MapDisplay from "@/components/Controls/Map.vue"
import CaseActions from "@/components/CaseDetails/CaseActions.vue"
import CaseTimeline from "./CaseTimeline.vue"
import History from "./History.vue"
import CCEventModal from "@/components/CaseDetails/CCEvent/CCEventModal.vue"
import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import FormGroup from "@/components/Fields/FormGroup.vue"
import CaseFilePreview from "@/components/CaseDetails/CaseFilePreview.vue"

import { LayoutLocations } from "@/types/templateLayout"
import CaseFile from "@/types/caseFile"
import { CaseEvents } from "@/types/originatingEvents"

import api from "@/services/api"
import { IntegrationOutOption } from "@/types/integration"
import FileService from "@/services/file-service"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { CadenceInstance } from "@/types/cadence"

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-header": CaseDetailsHeader,
		"case-fields": CaseFields,
		"case-actions": CaseActions,
		"map-display": MapDisplay,
		"case-files": CaseFiles,
		"key-entries": KeyEntries,
		"case-side-bar": CaseSideBar,
		"cc-event-modal": CCEventModal,
		"history": History,
		"case-timeline": CaseTimeline,
		"text-area-expand": TextAreaToggleExpand,
		"form-group": FormGroup,
		"file-preview": CaseFilePreview
	}
})
export default class Desktop extends Vue {
	@InjectReactive("fileService") fileService!: FileService

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Prop()
	public currentCase!: CaseDetails | null

	@Prop()
	public me!: User

	@Prop({ default: false })
	titleTemplateEnabled!: boolean

	@Prop({ default: false })
	canEditCurrentCase!: boolean

	@Prop({ default: true })
	canCreateComments!: boolean

	@Prop({ default: false })
	editAfterClosePermissions!: boolean

	@Prop()
	template!: TemplateDetails

	@Prop({ default: false })
	preventTemplateChange!: boolean

	@Prop({ default: false })
	hasMap!: boolean

	@Prop({ default: true })
	showAttachments!: boolean

	@Prop({ default: true })
	showTimeline!: boolean

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: true })
	showTitle!: boolean

	@Prop({ default: () => [] })
	caseFiles!: CaseFile[]

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents

	@Prop({ default: () => [] })
	caseIntegrationLinks!: IntegrationOutOption[]

	@Prop({ default: null })
	caseCadenceInstance!: CadenceInstance | null

	private savingFields: { [key: string]: boolean } = {
		"summary": false
	}
	private layoutLocations = LayoutLocations;

	/**
	 * Map of built-in fields user has lock on
	 */
	private get ownedLocks (): { [key: string]: boolean } {
		if (!this.currentCase || !this.currentCase.builtInFieldLocks) {
			return {}
		}

		return Object.keys(this.currentCase!.builtInFieldLocks!)
			.filter(key => this.currentCase!.builtInFieldLocks![key] && this.currentCase!.builtInFieldLocks![key]!.id === this.me!.id)
			.reduce((acc: { [key: string]: boolean }, key: string) => {
				acc[key] = true
				return acc
			}, {} as { [key: string]: boolean })
	}

	private get attachmentsVisible () {
		return (this.showAttachments && this.canUploadFiles && formatFieldsMixin.methods.showField(this.template, "attachments"))
	}

	templateIdChanged (templateId: string) {
		this.$emit("templateIdChanged", templateId)
	}

	caseChanged () {
		if (this.currentCase && this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	public makeClosedChanges () {
		this.$emit("makeClosedChanges")
	}

	close () {
		this.$emit("close")
	}

	integrationPush (outputOption: IntegrationOutOption) {
		this.$emit("integrationPush", outputOption)
	}

	private fieldGotFocus (fieldId: string) {
		if (this.currentCase!.id && this.canEditCurrentCase) {
			api.selectCaseField(this.currentCase!.id, fieldId, true)
		}
	}

	private async fieldLostFocus (fieldId: string, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.canEditCurrentCase) {
			try {
				this.savingFields[fieldId] = true
				await api.saveCaseProperty(this.currentCase!.id, fieldId, newValue)
			} finally {
				this.savingFields[fieldId] = false
			}
		}
	}

	// TODO extract into reusable function
	get recordOrCaseName () {
		if (this.currentCase?.isCase || this.template.alwaysPromoted) {
			return this.getCaseName
		}
		if (this.currentCase?.id) {
			return this.getRecordName
		}

		return `${this.getRecordName}/${this.getCaseName}`
	}

	get templateTitleFormatted () {
		return this.template ? `${this.template.title} ` : ""
	}
}
